import { useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { chunkAiRows, chunkReplyMatchesSource, mergeAiChunks, prepareAiRows, readAiChunkResponse } from '../lib/legacyAiAssist'
import { detectOldSystemExport } from '../lib/legacyImport'

export default function LegacyAiAssist({ mode, title, workshopId, sourceText = '', columns, onUse,
  onDirectPreview, validateRows }) {
  const [text, setText] = useState(sourceText)
  const [permission, setPermission] = useState(false)
  const [chunks, setChunks] = useState([])
  const [replies, setReplies] = useState([])
  const [editing, setEditing] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [fileName, setFileName] = useState('')
  const [format, setFormat] = useState('auto')
  const sourceRows = useMemo(() => prepareAiRows(text, { format }), [text, format])
  const detectedLane = useMemo(() => detectOldSystemExport(text), [text])
  const wrongLane = detectedLane && detectedLane !== mode
    && !(detectedLane === 'customers' && mode === 'customers')
  const directPreview = detectedLane === mode && typeof onDirectPreview === 'function'
  const complete = chunks.length > 0 && replies.length === chunks.length && replies.every(Boolean)
  const rowErrors = complete ? validateRows(editing) : []
  const ready = complete && editing.length === sourceRows.length && rowErrors.every(errors => !errors.length)

  function changeText(value) {
    if (busy) return
    setText(value)
    setPermission(false)
    setChunks([])
    setReplies([])
    setEditing([])
    setError('')
  }

  async function upload(event) {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      if (file.size > 500000) throw new Error('Keep uploads under 500 KB for review.')
      changeText(await file.text())
      setFileName(file.name)
    } catch (err) {
      setError(err.message)
    } finally {
      event.target.value = ''
    }
  }

  async function runChunk(index, currentChunks = chunks, currentReplies = replies) {
    const { data } = await supabase.auth.getSession()
    const token = data?.session?.access_token
    if (!token) throw new Error('Sign in again before analysis.')
    const response = await fetch('/.netlify/functions/ai-parse-legacy-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ workshopId, mode, ai_permission: true, fileName,
        source_rows: currentChunks[index] }),
    })
    const result = await readAiChunkResponse(response)
    if (result.mode !== mode) throw new Error('Analysis returned the wrong import lane. Retry this chunk.')
    if (!chunkReplyMatchesSource(currentChunks[index], result)) {
      throw new Error('Analysis omitted or duplicated source rows. Retry this chunk; no records were imported.')
    }
    const next = [...currentReplies]
    next[index] = result
    setReplies(next)
    return next
  }

  async function analyse() {
    if (!permission || busy) return
    setBusy(true)
    setError('')
    try {
      const nextChunks = chunkAiRows(sourceRows)
      if (!nextChunks.length) throw new Error('Add source rows before analysis.')
      setChunks(nextChunks)
      let nextReplies = Array(nextChunks.length).fill(null)
      setReplies(nextReplies)
      for (let index = 0; index < nextChunks.length; index++) {
        try {
          nextReplies = await runChunk(index, nextChunks, nextReplies)
        } catch (err) {
          throw new Error(`Chunk ${index + 1} of ${nextChunks.length}: ${err.message}`)
        }
      }
      setEditing(mergeAiChunks(nextChunks, nextReplies))
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function retryChunk(index) {
    if (busy || !permission) return
    setBusy(true)
    setError('')
    try {
      const next = await runChunk(index)
      if (next.every(Boolean)) setEditing(mergeAiChunks(chunks, next))
    } catch (err) {
      setError(`Chunk ${index + 1}: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  function splitChunk(index) {
    if (busy || chunks[index].length < 2) return
    const middle = Math.ceil(chunks[index].length / 2)
    setChunks(current => [...current.slice(0, index), current[index].slice(0, middle),
      current[index].slice(middle), ...current.slice(index + 1)])
    setReplies(current => [...current.slice(0, index), null, null, ...current.slice(index + 1)])
    setEditing([])
    setError('')
  }

  function editField(rowNumber, column, value) {
    setEditing(current => current.map(row => row.row_number === rowNumber
      ? { ...row, proposed: { ...row.proposed, [column]: value } } : row))
  }

  function previewRecognisedSource() {
    if (busy || !directPreview) return
    setError('')
    try {
      onDirectPreview(text, fileName)
    } catch (err) {
      setError(err.message || 'The recognised source could not be previewed.')
    }
  }

  return (
    <div className="legacy-ai-assist">
      <div className="card-title">{title} AI Assist</div>
      <div className="grid-2">
        <div className="field"><label>Upload messy CSV, TSV or text</label><input type="file" accept=".csv,.tsv,.txt,text/plain,text/csv" onChange={upload} disabled={busy} /></div>
        <div className="field"><label>Paste source</label><textarea value={text} onChange={e => changeText(e.target.value)} rows={3} disabled={busy} /></div>
      </div>
      {sourceText && sourceText !== text && <button className="btn btn-ghost" onClick={() => changeText(sourceText)} disabled={busy}>Use current loader source</button>}
      <div className="field"><label>Source grouping</label><select value={format} disabled={busy}
        onChange={e => { setFormat(e.target.value); setChunks([]); setReplies([]); setEditing([]); setPermission(false) }}>
        <option value="auto">Detect table or text lines</option>
        <option value="table">CSV / TSV table</option>
        <option value="text_lines">One record per line</option>
        <option value="text_blocks">One record per blank-separated block</option>
      </select></div>
      {directPreview ? <div className="alert alert-info">Recognised {title} export. AI analysis is not required; preview it directly without sending the source to Anthropic.</div>
        : <label className="legacy-ai-consent"><input type="checkbox" checked={permission} disabled={busy}
          onChange={e => { setPermission(e.target.checked); if (!e.target.checked) { setChunks([]); setReplies([]); setEditing([]) } }} /> I permit this source to be sent to Anthropic for mapping. Analysis will not import records.</label>}
      {wrongLane && <div className="alert alert-error">This looks like a {detectedLane === 'historic_jobs' ? 'historical job' : 'customer'} export. Use that loader's AI Assist so facts cannot be misfiled.</div>}
      <div className="action-row">{directPreview
        ? <button className="btn btn-primary" onClick={previewRecognisedSource} disabled={!sourceRows.length || busy}>Preview {sourceRows.length} {title}</button>
        : <button className="btn btn-ghost" onClick={analyse} disabled={!permission || !sourceRows.length || busy || wrongLane}>Analyse {sourceRows.length} rows</button>}</div>
      {error && <div className="alert alert-error">{error}</div>}
      {chunks.length > 0 && <div className="text-muted fs12">Original rows: {sourceRows.length}. Mapped: {replies.flatMap(reply => reply?.rows || []).length}. Chunks: {chunks.length}. No approval is possible until every row is mapped.</div>}
      {chunks.map((chunk, index) => !replies[index] && <div key={index} className="action-row">Chunk {index + 1}: rows {chunk[0].row_number}-{chunk[chunk.length - 1].row_number}
        <button className="btn btn-ghost" onClick={() => retryChunk(index)} disabled={!permission || busy}>Retry chunk</button>
        {chunk.length > 1 && <button className="btn btn-ghost" onClick={() => splitChunk(index)} disabled={busy}>Split chunk</button>}</div>)}
      {complete && <div className="legacy-ai-preview">
        <div className="text-muted fs12">Proposed rows: {editing.length}. Review the source and editable mapping below; ignored fields and uncertainties are not imported.</div>
        {editing.map((row, index) => <div className="legacy-ai-row" key={row.row_number}>
          <div className="card-title">Source row {row.row_number}</div>
          <div className="grid-2">
            <div><div className="text-muted fs12">Source fields</div>{Object.entries(row.source_fields).map(([key, value]) => <div className="detail-row" key={key}><span>{key}</span><span>{value || '-'}</span></div>)}</div>
            <div><div className="text-muted fs12">Proposed Rebound fields</div>{columns.map(column => <div className="field" key={column}><label>{column.replace(/_/g, ' ')} {Object.entries(row.field_mapping || {}).filter(([, target]) => target === column).map(([heading]) => `← ${heading}`).join(', ')}</label><input value={row.proposed?.[column] || ''} onChange={e => editField(row.row_number, column, e.target.value)} /></div>)}</div>
          </div>
          <div className="text-muted fs12">Ignored: {row.ignored_fields?.join(', ') || 'none'} · Uncertainties: {row.uncertainties?.join('; ') || 'none'}</div>
          {(rowErrors[index] || []).map(message => <div className="alert alert-error" key={message}>{message}</div>)}
        </div>)}
        <button className="btn btn-primary" onClick={() => onUse(editing)} disabled={!ready}>Use reviewed proposal in {title} preview</button>
      </div>}
    </div>
  )
}
